import { useCallback } from 'react';
import { useAuth } from 'src/hooks/useAuth';
import axiosInstance, { endpoints } from 'src/utils/axios';

/**
 * useActivityLogger
 *
 * Fire-and-forget hook for logging user page visits and actions
 * to the Admin Activity Audit Log.
 *
 * Usage:
 *   const { logVisit } = useActivityLogger();
 *
 *   // In useEffect on page mount:
 *   useEffect(() => {
 *     logVisit('attendance', 'Attendance', 'My Attendance');
 *   }, [logVisit]);
 *
 * Notes:
 * - Only fires if the org has activityLogEnabled === true (checked server-side).
 * - Silently swallows errors — never blocks the UI.
 * - The logVisit function is memoized with useCallback for safe useEffect deps.
 */
export function useActivityLogger() {
  const { user } = useAuth();

  /**
   * Log a page visit event.
   * @param {string} module - Module key, e.g. 'attendance'
   * @param {string} menu   - Human-readable menu name, e.g. 'Attendance'
   * @param {string} [subMenu] - Sub-page context, e.g. 'My Attendance'
   */
  const logVisit = useCallback(
    (module, menu, subMenu = null) => {
      // Only log for admin/moderator roles
      if (!user?.role || !['admin', 'moderator'].includes(user.role)) return;

      // Fire-and-forget: Don't await, don't block
      axiosInstance
        .post(endpoints.activityLog.logVisit, { module, menu, subMenu })
        .catch(() => {
          // Silently swallow — logging must never break the UI
        });
    },
    [user]
  );

  return { logVisit };
}
